import { backspaceInput } from '../lib/emojiBackspace';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TrimRange } from './VideoTrimEditor';
import { backdropClose } from '../lib/modalBackdrop';
import { classifyFile } from '../lib/mediaHelpers';

// Полный эмодзи-пикер (1911 эмодзи) — не нужен, пока не открыт (см. также ChatView.tsx,
// где применён тот же приём) — выносим в отдельный чанк.
const EmojiPicker = lazy(() => import('./EmojiPicker'));
// Редактор обрезки видео — тоже в отдельный чанк: нужен только если среди вложений есть
// видео И пользователь явно открыл "Обрезать" (не при каждом прикреплении файла).
const VideoTrimEditor = lazy(() => import('./VideoTrimEditor'));
// Редактор изображений (поворот + кроп, см. отчёт пользователя "добавь редактор картинок при
// добавлении изображений") — аналогично, отдельный чанк, только по явному клику "Редактировать".
const ImageEditor = lazy(() => import('./ImageEditor'));

export type StagedKind = 'image' | 'video' | 'audio' | 'file';

export type AttachmentSource = 'photo' | 'document' | 'other';

export interface StagedAttachment {
  file: File;
  source: AttachmentSource;
  trim?: TrimRange;
}

interface StagedFile extends StagedAttachment {
  kind: StagedKind;
  previewUrl: string | null;
}

interface Props {
  files: StagedAttachment[];
  onCancel: () => void;
  onSend: (items: StagedAttachment[], caption: string) => Promise<string | null>;
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

/**
 * Как в Telegram: перетащили/выбрали разом несколько файлов — один диалог со списком,
 * подписью и отправкой одним сообщением (payload.data.media[], см. ChatView.renderMedia).
 * Файлы можно убирать по одному; превью картинок/видео — через object URL, без загрузки
 * на сервер до нажатия "Отправить".
 */
export default function AttachPreviewModal({ files, onCancel, onSend }: Props) {
  const { t } = useTranslation('attach');
  const [items, setItems] = useState<StagedFile[]>([]);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [trimIndex, setTrimIndex] = useState<number | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  // "⋮"-меню строки вложения ("Обрезать" для видео / "Редактировать" для изображений) — тот же
  // паттерн row-menu/ctx-item, что и в AdminPanel, вместо отдельной кнопки в каждой строке.
  const [attachMenuIndex, setAttachMenuIndex] = useState<number | null>(null);
  // position: fixed с координатами во ВЬЮПОРТЕ (та же идея, что у ctx-menu/openContextMenu в
  // ChatView.tsx), а не absolute внутри строки. Раньше меню позиционировалось относительно
  // своей строки и решало "вверх/вниз" по остатку места ДО НИЗА .attach-preview-list — но сам
  // список скроллится (overflow-y: auto) и обрезает абсолютно спозиционированных потомков по
  // ЛЮБОЙ своей границе, не только нижней: при открытии вверх у первой/единственной строки
  // (мало вложений — самый частый случай) меню вылезало выше списка и пряталось под шапку
  // модалки (см. отчёт пользователя "кнопка для редактирования уходит под верхнюю панель
  // меню"). position:fixed целиком обходит эту обрезку.
  const [attachMenuPos, setAttachMenuPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const ATTACH_MENU_H = 44; // одна строка меню — либо "Обрезать", либо "Редактировать"
  const openAttachMenu = (i: number, e: React.MouseEvent<HTMLButtonElement>) => {
    const open = attachMenuIndex !== i;
    setAttachMenuIndex(open ? i : null);
    if (!open) {
      setAttachMenuPos(null);
      return;
    }
    const btnRect = e.currentTarget.getBoundingClientRect();
    const right = window.innerWidth - btnRect.right;
    const roomBelow = window.innerHeight - btnRect.bottom;
    setAttachMenuPos(
      roomBelow >= ATTACH_MENU_H + 8
        ? { top: btnRect.bottom + 4, right }
        : { bottom: window.innerHeight - btnRect.top + 4, right },
    );
  };
  const closeAttachMenu = () => {
    setAttachMenuIndex(null);
    setAttachMenuPos(null);
  };
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const staged = files.map(({ file, source }) => {
      const kind = classifyFile(file);
      const previewUrl = kind === 'image' || kind === 'video' ? URL.createObjectURL(file) : null;
      return { file, source, kind, previewUrl };
    });
    setItems(staged);
    return () => {
      staged.forEach((s) => {
        if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
      });
    };
  }, [files]);

  const removeAt = (i: number) => {
    if (busy) return;
    if (items.length <= 1) {
      onCancel();
      return;
    }
    const removed = items[i];
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  };

  const doSend = async () => {
    if (items.length === 0 || busy) return;
    setBusy(true);
    setSendError(null);
    try {
      const message = await onSend(items.map((it) => ({ file: it.file, source: it.source, trim: it.trim })), caption.trim());
      if (message) {
        setSendError(message);
        setBusy(false);
      }
    } catch {
      setSendError(t('sendFailed'));
      setBusy(false);
    }
  };

  const saveTrim = (range: TrimRange | null) => {
    if (trimIndex === null) return;
    // null — метаданные видео не загрузились, обрезка не применена: просто закрываем
    // редактор, не трогая уже сохранённый trim (иначе на сервер ушёл бы {start:0,end:0}).
    if (range) setItems((prev) => prev.map((it, idx) => (idx === trimIndex ? { ...it, trim: range } : it)));
    setTrimIndex(null);
  };

  const saveEdited = (blob: Blob) => {
    if (editIndex === null) return;
    setItems((prev) => prev.map((it, idx) => {
      if (idx !== editIndex) return it;
      if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      const file = new File([blob], it.file.name, { type: blob.type });
      return { ...it, file, previewUrl: URL.createObjectURL(file) };
    }));
    setEditIndex(null);
  };

  const insertEmoji = (e: string) => {
    setCaption((t) => t + e);
    setEmojiOpen(false);
    setTimeout(() => taRef.current?.focus(), 0);
  };

  if (items.length === 0) return null;

  return (
    <div className="modal-backdrop" {...backdropClose(busy ? undefined : onCancel)}>
      <div className="modal attach-preview-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" title={t('close')} disabled={busy} onClick={onCancel}>✕</button>
        <div className="attach-preview-header">
          {items.length === 1 ? items[0].file.name : t('fileCount', { count: items.length })}
        </div>
        <div className="attach-preview-list custom-scroll">
          {items.map((it, i) => (
            <div className="attach-preview-row" key={it.file.name + ':' + i}>
              {it.previewUrl ? (
                it.kind === 'video' ? (
                  <video src={it.previewUrl} className="attach-preview-thumb" muted />
                ) : (
                  <img src={it.previewUrl} className="attach-preview-thumb" alt="" />
                )
              ) : (
                <img
                  src={it.kind === 'audio' ? '/audio-file.svg' : '/document.svg'}
                  className="attach-preview-icon"
                  alt=""
                />
              )}
              <div className="doc-info" style={{ flex: 1, minWidth: 0 }}>
                <div className="doc-name">{it.file.name}</div>
                <div className="doc-size">
                  {formatSize(it.file.size)}
                  {it.trim && t('trimmed', { seconds: Math.round(it.trim.end - it.trim.start) })}
                </div>
                {it.source === 'photo' && <div className="doc-size" style={{ color: 'var(--muted)' }}>{t('compressionHint')}</div>}
              </div>
              {(it.kind === 'video' || it.kind === 'image') && (
                <div className="row-menu-wrap">
                  <button
                    type="button"
                    className="row-menu-btn"
                    title={t('actions')}
                    disabled={busy}
                    onClick={(e) => openAttachMenu(i, e)}
                  >
                    ⋮
                  </button>
                  {attachMenuIndex === i && attachMenuPos && (
                    <>
                      <div className="row-menu-backdrop" onClick={closeAttachMenu} />
                      <div
                        className="row-menu row-menu-fixed"
                        style={{ top: attachMenuPos.top, bottom: attachMenuPos.bottom, right: attachMenuPos.right }}
                      >
                        {it.kind === 'video' ? (
                          <button
                            type="button"
                            className="ctx-item"
                            onClick={() => {
                              closeAttachMenu();
                              setTrimIndex(i);
                            }}
                          >
                            {t('trim')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="ctx-item"
                            onClick={() => {
                              closeAttachMenu();
                              setEditIndex(i);
                            }}
                          >
                            {t('edit')}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
              <button
                type="button"
                className="pinned-banner-close"
                title={t('remove')}
                disabled={busy}
                onClick={() => removeAt(i)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        {sendError && <div role="alert" className="error" style={{ padding: '0 16px 8px' }}>{sendError}</div>}
        <div className="composer attach-preview-composer">
          <div className="ta-wrap">
            <textarea
              ref={taRef}
              placeholder={t('messagePlaceholder')}
              value={caption}
              disabled={busy}
              rows={1}
              onChange={(e) => setCaption(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  doSend();
                }
              }}
            />
            <button
              type="button"
              className="emoji-toggle-btn"
              title={t('emoji')}
              disabled={busy}
              onClick={() => setEmojiOpen((v) => !v)}
            >
              <img src="/emoji.svg" alt={t('emoji')} />
            </button>
            {emojiOpen && (
              <Suspense fallback={null}>
                <EmojiPicker onBackspace={() => setCaption(backspaceInput(caption, taRef.current))} onSelect={insertEmoji} onClose={() => setEmojiOpen(false)} />
              </Suspense>
            )}
          </div>
          <button type="button" className={'send-btn' + (busy ? ' empty' : '')} disabled={busy} title={t('send')} onClick={doSend}>
            <img src="/send.svg" alt={t('send')} />
          </button>
        </div>
      </div>
      {trimIndex !== null && items[trimIndex] && (
        <Suspense fallback={null}>
          <VideoTrimEditor
            file={items[trimIndex].file}
            initial={items[trimIndex].trim}
            onSave={saveTrim}
          />
        </Suspense>
      )}
      {editIndex !== null && items[editIndex] && (
        <Suspense fallback={null}>
          <ImageEditor
            file={items[editIndex].file}
            onCancel={() => setEditIndex(null)}
            onSave={saveEdited}
          />
        </Suspense>
      )}
    </div>
  );
}
